/**
 * MapViewCard - Info card shown in map marker popup.
 * Shows thumbnail, view name, status, GPS info with progressive messages and accuracy badge.
 */

import { View, Photo } from '@/types';
import { formatTimeAgo } from '@/utils/dateUtils';
import { Spin } from 'antd';

/** Accuracy threshold in meters — above this, show yellow warning badge */
const ACCURACY_WARNING_THRESHOLD = 20;

interface MapViewCardProps {
  view: View;
  latestPhoto: Photo | null;
  connectionStatus: 'online' | 'offline' | 'survival' | 'unpaired' | 'never_connected';
  isGpsLoading?: boolean;
  gpsProgressMessage?: string;
  gpsError?: string;
}

export function MapViewCard({ view, latestPhoto, connectionStatus, isGpsLoading, gpsProgressMessage, gpsError }: MapViewCardProps) {
  const isOnline = connectionStatus === 'online';
  const timerSettings = view.timerSettings;
  // GPS location age
  const gpsLocation = view.gpsLocation;
  const locationAge = gpsLocation?.timestamp
    ? formatTimeAgo(gpsLocation.timestamp.toDate())
    : null;
  const accuracy = gpsLocation?.accuracy;
  const showAccuracyWarning = accuracy != null && accuracy > ACCURACY_WARNING_THRESHOLD;

  return (
    <div style={{ width: 220, fontSize: 12, lineHeight: 1.5, padding: 4 }}>
      {/* Thumbnail */}
      {latestPhoto ? (
        <img
          src={latestPhoto.urls.thumb200}
          alt={view.name}
          style={{
            width: '100%',
            height: 120,
            objectFit: 'cover',
            borderRadius: 4,
            marginBottom: 6,
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: 120,
            background: '#f0f0f0',
            borderRadius: 4,
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
          }}
        >
          No photo
        </div>
      )}

      {/* View name */}
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{view.name}</div>

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: isOnline ? '#52c41a' : '#ff4d4f',
            display: 'inline-block',
          }}
        />
        <span>{isOnline ? 'Online' : 'Offline'}</span>
        {view.capturing && (
          <span style={{ color: '#1890ff', marginLeft: 4 }}>
            &bull; Capturing
          </span>
        )}
      </div>

      {/* Parameters */}
      <div style={{ color: '#666', marginTop: 4 }}>
        {timerSettings && (
          <div>Interval: {timerSettings.interval}s</div>
        )}
        {latestPhoto?.capturedAt && (
          <div>Last shot: {formatTimeAgo(latestPhoto.capturedAt.toDate())}</div>
        )}
      </div>

      {/* GPS info */}
      {isGpsLoading ? (
        <div style={{ marginTop: 4, color: '#1890ff' }}>
          <Spin size="small" /> {gpsProgressMessage || 'Requesting position...'}
        </div>
      ) : gpsError ? (
        <div style={{ marginTop: 4, color: '#ff4d4f', fontSize: 11 }}>
          {gpsError}
        </div>
      ) : locationAge ? (
        <div style={{ marginTop: 4, fontSize: 11 }}>
          <span style={{ color: '#999' }}>Position: {locationAge}</span>
          {showAccuracyWarning && (
            <span
              style={{
                marginLeft: 6,
                padding: '1px 5px',
                borderRadius: 3,
                backgroundColor: '#fff7e6',
                color: '#d48806',
                fontSize: 10,
                fontWeight: 500,
              }}
            >
              ~{Math.round(accuracy)}m accuracy
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
